import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import API, { formatApiError } from "../../lib/api";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../ui/alert-dialog";
import { HardHat, WarningCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

const LABEL = { job: "job", emergency_job: "emergency job" };

export function RamsGuardDialog({ open, entityType, entityId, onContinue, onCancel }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const createFirst = async () => {
    setBusy(true);
    try {
      const { data } = await API.post("/hs/rams", { entity_type: entityType, entity_id: entityId });
      toast.success(`${data.reference} created — issue it before the crew is on site`);
      onCancel();
      navigate(`/health-safety/rams/${data.id}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <AlertDialog open={open} onOpenChange={(o) => { if (!o) onCancel(); }}>
      <AlertDialogContent data-testid="rams-guard-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2 font-[Chivo]"><WarningCircle size={20} weight="fill" className="text-red-600" /> No RAMS on this {LABEL[entityType] || "job"} yet</AlertDialogTitle>
          <AlertDialogDescription>
            You can still dispatch, but a Risk Assessment &amp; Method Statement should be issued before anyone is on site. The job will show a <span className="font-semibold text-red-700">No RAMS</span> flag until one is issued.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="flex-col sm:flex-row gap-2">
          <AlertDialogCancel data-testid="rams-guard-cancel">Cancel</AlertDialogCancel>
          <button type="button" onClick={createFirst} disabled={busy} className="inline-flex items-center justify-center gap-1 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-900 hover:bg-amber-100 dark:bg-amber-950/30 dark:text-amber-200 disabled:opacity-50" data-testid="rams-guard-create">
            <HardHat size={14} /> {busy ? "Creating…" : "Create RAMS first"}
          </button>
          <AlertDialogAction onClick={onContinue} data-testid="rams-guard-continue">Continue without RAMS</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/** Wraps a dispatch action (assigning crew / supplier). When the job has no RAMS at all, asks "Continue without RAMS?" once per job per session. */
export function useRamsGuard({ entityType, entityId, hasRams }) {
  const [pending, setPending] = useState(null);
  const key = `rams-guard-${entityId}`;
  const guard = useCallback((fn) => {
    if (hasRams === undefined || hasRams || sessionStorage.getItem(key)) return fn();
    setPending(() => fn);
  }, [hasRams, key]);
  const dialog = (
    <RamsGuardDialog open={!!pending} entityType={entityType} entityId={entityId}
      onContinue={() => { sessionStorage.setItem(key, "1"); const fn = pending; setPending(null); fn && fn(); }}
      onCancel={() => setPending(null)} />
  );
  return { guard, dialog };
}
